import * as React from "react";
import {
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";
import { ResourceDWR } from "../../../../dto/ResourceDWR";
import { LoaderData } from "../MyAccount";
import { getLoginRedirect } from "../../../auth/getLoginRedirect";
import { AccountHolder, useAccount } from "../../../Account";
import { ShareControl } from "@tlc/ls2pac/app/controls/ShareControl";
import { useLanguage } from "../../../Language";
import {
  Alert,
  AlertTitle,
  alpha,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Paper,
  SelectChangeEvent,
  TablePagination,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import PrintIcon from "@mui/icons-material/Print";
import { TLCEventManager } from "../../../../events/EventManager";
import { LS2PAC_CONSTANTS } from "src/main/resources/js/main/constants";
import { useGlobalConfig } from "../../../GlobalConfig";
import { PrintList } from "@tlc/ls2pac/app/utils/PrintList";
import { PrintBib } from "@tlc/ls2pac/app/utils/PrintBib";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { SimpleErrorDialog } from "@tlc/ls2pac/app/controls/simpleErrorDialog";
import { ResourceListItem } from "./ResourceListItem";
import { AssociatedStudents } from "./AssociatedStudents";
import { DeleteListDialog } from "./DeleteListDialog";
import { RenameListDialog } from "./RenameListDialog";
import {
  ListSortType,
  SavedListSortControl,
  SearchSortType,
} from "./SavedListSortControl";
import { ListService, ListType } from "../ListService";

export async function listLoader({ params, request }: LoaderFunctionArgs) {
  const { isAuthenticated } = await AccountHolder.fetchAccountData();

  if (!isAuthenticated) {
    return redirect(getLoginRedirect(request));
  }

  const { allLists } = await ListService.getAllLists();
  try {
    const listId = params.listId === "null" ? null : parseInt(params.listId);
    const summary = allLists.find((list) => list.id === listId);
    if (!summary) {
      return redirect("/404");
    }
    const sortType =
      summary.type === ListType.Prohibited
        ? SearchSortType.Title
        : ListSortType.Title;
    const listContent =
      listId !== null
        ? await ListService.getListResources(
            listId,
            summary.type,
            0,
            10,
            sortType
          )
        : null;
    return { summary, allLists, listContent };
  } catch (e) {
    return redirect("/404");
  }
}

export const SavedListsPage: React.FC = () => {
  const { summary, allLists, listContent } = useLoaderData() as LoaderData<
    typeof listLoader
  >;

  return (
    <SavedList
      key={summary.id}
      summary={summary}
      allLists={allLists}
      listContent={listContent}
    ></SavedList>
  );
};

const SavedList: React.FC<LoaderData<typeof listLoader>> = ({
  summary,
  allLists,
  listContent,
}) => {
  const { translate } = useLanguage();
  const navigate = useNavigate();
  const { globalSettings } = useGlobalConfig();
  const { patron } = useAccount();

  const [selectedList, setSelectedList] = React.useState(summary);
  const [listOptions, setListOptions] = React.useState(allLists);
  const [isRenameOpen, setRenameOpen] = React.useState<boolean>(false);
  const [isDeleteOpen, setDeleteOpen] = React.useState<boolean>(false);
  const [selected, setSelected] = React.useState<ResourceDWR[]>([]);
  const [page, setPage] = React.useState<number>(0);
  const [total, setTotal] = React.useState<number>(listContent?.totalHits);
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [sort, setSort] = React.useState(ListSortType.Title);
  const [displayedRows, setDisplayedRows] = React.useState<ResourceDWR[]>(
    listContent?.resources || []
  );

  const selectedResources = React.useMemo<number[]>(
    () => selected.map((resource) => resource.id),
    [selected]
  );

  const isListEmpty = React.useMemo(
    () => !selectedList.id || total === 0,
    [total]
  );

  const printListDialog = React.useRef<PrintList | null>(null);
  const printBibDialog = React.useRef<PrintBib | null>(null);
  React.useEffect(() => {
    const shareControl = new ShareControl();
    printListDialog.current = new PrintList();
    printBibDialog.current = new PrintBib();

    return () => {
      shareControl?.cleanup();
      // TODO expose cleanup function on PrintList
      // TODO expose cleanup function on PrintBib
    };
  }, [summary.id]);

  const savedListPageContainerRef = React.useRef<HTMLDivElement>(null);

  function ScrollToTop() {
    savedListPageContainerRef.current?.scrollIntoView({ behavior: "instant" });
  }

  const updateListName = function (listName: string) {
    setListOptions(
      listOptions.map((list) => {
        if (selectedList.id === list.id) {
          const updatedList = {
            ...list,
            name: listName,
          };
          setSelectedList(updatedList);
          return updatedList;
        }
        return list;
      })
    );
  };

  const updateListCount = function (count: number) {
    setListOptions(
      listOptions.map((list) => {
        if (selectedList.id === list.id) {
          const updatedList = {
            ...list,
            count,
          };
          setSelectedList(updatedList);
          return updatedList;
        }
        return list;
      })
    );
  };

  const handleSortChange = async function (event: SelectChangeEvent<unknown>) {
    const newSort = event.target.value as ListSortType;
    setSort(newSort);
    setPage(0);
    const { resources } = await ListService.getListResources(
      summary.id,
      summary.type,
      0,
      pageSize,
      newSort
    );
    setDisplayedRows(resources);
  };

  const onClickDelete = async (
    event: React.MouseEvent,
    resourceId: number | null = null
  ) => {
    // kind of surprised there isn't a confirmation here
    try {
      const itemsToDelete = resourceId ? [resourceId] : selectedResources;
      const success = await ListService.removeListItems(
        summary.id,
        selectedList.type,
        itemsToDelete
      );
      if (success) {
        const deletedCount = itemsToDelete.length;
        const newCount = total - deletedCount;
        const pageStart = page * pageSize;
        const newPage =
          newCount <= 0
            ? 0
            : newCount <= pageStart
            ? Math.ceil(newCount / pageSize) - 1
            : page;
        // loading state?
        // TODO call handlePageChange instead?
        const { resources, totalHits } = await ListService.getListResources(
          summary.id,
          summary.type,
          newPage,
          pageSize,
          sort
        );
        updateListCount(totalHits);
        setPage(newPage);
        setDisplayedRows(resources);
        setTotal(totalHits);
        if (!resourceId) {
          setSelected([]);
        }
        SuccessMessageControl.show(
          `${deletedCount} ${translate("removedItemFromListSuccess")}`
        );
      } else {
        SimpleErrorDialog.show(translate("removedItemFromListError"));
      }
    } catch (e) {
      console.error(e);
      SimpleErrorDialog.show(translate("removedItemFromListError"));
    }
  };

  const getAllResourcesOfList = async () => {
    const { resources } = await ListService.getListResources(
      summary.id,
      summary.type,
      0,
      total,
      sort
    );
    return resources;
  };

  const handleSelectAllClick = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      const { resources } = await ListService.getListResources(
        summary.id,
        summary.type,
        0,
        total,
        sort
      );
      setSelected(resources);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    setPage(newPage);
    const { resources } = await ListService.getListResources(
      summary.id,
      summary.type,
      newPage,
      pageSize,
      sort
    );
    setDisplayedRows(resources);

    ScrollToTop();
  };

  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const size = parseInt(event.target.value, 10);
    setPageSize(size);
    setPage(0);
    const { resources } = await ListService.getListResources(
      summary.id,
      summary.type,
      0,
      size,
      sort
    );
    setDisplayedRows(resources);

    ScrollToTop();
  };

  const isSelected = (id: number) => selectedResources.indexOf(id) !== -1;

  const handleClick = (
    event: React.MouseEvent<unknown>,
    resource: ResourceDWR
  ) => {
    const { id } = resource;
    const selectedIndex = selectedResources.indexOf(id);

    if (selectedIndex === -1) {
      setSelected([...selected, resource]);
    } else {
      setSelected(selected.filter((tc) => tc.id !== id));
    }
  };

  return (
    <Box key={summary.id} ref={savedListPageContainerRef}>
      <Toolbar sx={{ flexWrap: "wrap", gap: 1 }}>
        <Autocomplete
          key={summary.id}
          sx={{ width: "300px" }}
          value={selectedList}
          disableClearable={true}
          options={listOptions}
          onChange={(event, value) => {
            event.preventDefault();
            navigate(value.pathName);
          }}
          getOptionKey={(option) => option.pathName}
          groupBy={(option) => option.type}
          getOptionLabel={(option) => {
            if (option.id === null) {
              // empty list
              return option.name;
            }
            return `${option.name} (${option.count})`;
          }}
          renderInput={(params) => <TextField {...params} label="List" />}
        ></Autocomplete>
        <Button disabled={!selectedList.id} onClick={() => setRenameOpen(true)}>
          {translate("rename")}
        </Button>
        {translate("or")}
        <Button disabled={!selectedList.id} onClick={() => setDeleteOpen(true)}>
          {translate("delete")}
        </Button>
      </Toolbar>
      <Toolbar>
        <Box>
          <Button
            disabled={isListEmpty}
            onClick={() => {
              printBibDialog.current.openPrintDialog(async function () {
                const resources = await getAllResourcesOfList();
                printBibDialog.current.print({
                  ...summary,
                  resources,
                });
              });
            }}
          >
            {translate("viewBibliography")}
          </Button>
          {globalSettings?.sharingEnabled && (
            <Button
              disabled={isListEmpty || summary.type === ListType.Prohibited}
              onClick={(event) => {
                TLCEventManager.fireEvent(
                  LS2PAC_CONSTANTS.Events.onOpenShareThis,
                  {
                    element: event.currentTarget,
                    url: summary.id,
                    shareType: LS2PAC_CONSTANTS.shareTypes.staticList,
                  }
                );
                return false;
              }}
            >
              {translate("shareThis")}
            </Button>
          )}
          <Button
            disabled={isListEmpty || summary.type === ListType.Prohibited}
            component="a"
            target="_blank"
            href={`/list/static/${summary.id}/rss`}
            startIcon={<RssFeedIcon />}
          >
            {translate("receiveUpdatesToList")}
          </Button>
          <Button
            disabled={isListEmpty}
            onClick={() => {
              printListDialog.current.openPrintDialog(async () => {
                const resources = await getAllResourcesOfList();
                printListDialog.current.print(resources, summary.name);
              });
            }}
            startIcon={<PrintIcon />}
          >
            {translate("printList")}
          </Button>
        </Box>
      </Toolbar>
      {summary.type === ListType.Prohibited && (
        <Toolbar>
          <AssociatedStudents
            key={summary.id}
            childIds={listContent?.childIds || []}
            students={patron.children}
            listId={summary.id}
          ></AssociatedStudents>
        </Toolbar>
      )}
      {isListEmpty && (
        <Alert severity="info">
          <AlertTitle>{translate("noListsMessage")}</AlertTitle>
          <ul>
            <li>{translate("noListsMessageTip1")}</li>
            <li>{translate("noListsMessageTip2")}</li>
          </ul>
        </Alert>
      )}
      {!isListEmpty && (
        <Paper sx={{ width: "100%", mb: 2 }}>
          <Toolbar
            sx={{
              flexWrap: "wrap",
              gap: 1,
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
              ...(selected.length > 0 && {
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    theme.palette.action.activatedOpacity
                  ),
              }),
            }}
          >
            <Checkbox
              color="primary"
              indeterminate={selected.length > 0 && selected.length < total}
              checked={total > 0 && selected.length === total}
              onChange={handleSelectAllClick}
              slotProps={{
                input: {
                  "aria-label": translate("selectAll"),
                },
              }}
            />
            {selected.length > 0 ? (
              <Typography
                sx={{ flex: "1 1", textWrap: "nowrap" }}
                color="inherit"
                variant="subtitle1"
                component="div"
              >
                {selected.length} {translate("selected")}
              </Typography>
            ) : (
              <Typography
                sx={{ flex: "1 1", textWrap: "nowrap" }}
                variant="h6"
                id="tableTitle"
                component="div"
              >
                {total} {translate("resources")}
              </Typography>
            )}
            {selected.length === 0 && (
              <SavedListSortControl
                value={sort}
                onChange={handleSortChange}
              ></SavedListSortControl>
            )}
            {selected.length > 0 && (
              <>
                <Button onClick={onClickDelete}>
                  {translate("removeFromList")}
                </Button>
                <Button
                  onClick={(event) => {
                    TLCEventManager.fireEvent(
                      LS2PAC_CONSTANTS.Events.onRequest,
                      {
                        resource: selected,
                        referrer: "MyLists",
                      }
                    );
                  }}
                >
                  {translate("placeRequest")}
                </Button>
              </>
            )}
          </Toolbar>
          <div className="resultItems">
            <ul>
              {displayedRows.map((row, index) => {
                const isItemSelected = isSelected(row.id);

                return (
                  <li key={row.id} className="item mainResultItem clearfix">
                    <Box>
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onClick={(event) => {
                          handleClick(event, row);
                        }}
                        slotProps={{
                          input: {
                            "aria-label": translate("select"),
                          },
                        }}
                      />
                    </Box>

                    <ResourceListItem
                      onClickRemove={(event) => onClickDelete(event, row.id)}
                      resource={row}
                      key={row.id}
                    ></ResourceListItem>
                  </li>
                );
              })}
            </ul>
          </div>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            labelRowsPerPage={translate("size")}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      <DeleteListDialog
        open={isDeleteOpen}
        onClose={async (deleted) => {
          setDeleteOpen(false);
          if (deleted === true) {
            const path = await ListService.getDefaultListPath();
            navigate(path);
          }
        }}
        listId={summary.id}
        listType={summary.type}
        successMessage={translate("removedListSuccess")}
        errorMessage={translate("removedListError")}
      ></DeleteListDialog>

      <RenameListDialog
        name={summary.name}
        id={summary.id}
        type={summary.type}
        title={translate("renameListDialogTitle")}
        successMessage={translate("renamedListSuccess")}
        errorMessage={translate("renamedListError")}
        open={isRenameOpen}
        onClose={() => setRenameOpen(false)}
        onSave={(newListName) => {
          updateListName(newListName);
          setRenameOpen(false);
        }}
      ></RenameListDialog>
    </Box>
  );
};
